import dayjs from 'utils/dayjs';

import { parseDate } from './date';
import {
  DEFAULT_API_DATE_FORMAT,
  DEFAULT_MOBILE_DATE_FORMAT,
} from './defaultValues';

export const MB_ARCHIVE_DATETIME_FORMAT = 'DD.MM.YYYY HH:mm:ss';
export const MB_ARCHIVE_RANGE_LABEL_FORMAT = 'DD.MM.YYYY';
export const MB_ARCHIVE_DAY_TIME_FORMAT = 'HH:mm';

const parseMbArchiveDate = parseDate;

export const formatMbArchiveDateParam = value =>
  parseMbArchiveDate(value)?.format(DEFAULT_API_DATE_FORMAT);

export const getMbPath = currency => {
  const normalized = currency?.toLowerCase();
  const slug = !normalized || normalized === 'usd' ? '' : `${normalized}/`;

  return `/mb/${slug}`;
};

export const getMbArchiveDayPath = (currency, date) => {
  const formattedDate = formatMbArchiveDateParam(date);

  if (!formattedDate) {
    return null;
  }

  return `/mb/archive/${currency.toLowerCase()}/${formattedDate}/`;
};

export const normalizeMbArchiveRange = (from, to) => {
  const start = parseMbArchiveDate(from);
  const end = parseMbArchiveDate(to);

  if (!start?.isValid() || !end?.isValid()) {
    return null;
  }

  const dateFrom = start.format(DEFAULT_API_DATE_FORMAT);
  const dateTo = end.format(DEFAULT_API_DATE_FORMAT);

  if (end.isBefore(start, 'day')) {
    return { dateFrom: dateTo, dateTo: dateFrom };
  }

  return { dateFrom, dateTo };
};

export const getMbArchiveRangePath = (currency, from, to) => {
  const range = normalizeMbArchiveRange(from, to);

  if (!range) {
    return null;
  }

  return `/mb/archive/${currency.toLowerCase()}/${range.dateFrom}/${
    range.dateTo
  }/`;
};

export const getMbArchiveTabPath = ({
  tabCurrency,
  date,
  dateFrom,
  dateTo,
}) => {
  if (dateTo) {
    return `/mb/archive/${tabCurrency}/${dateFrom}/${dateTo}/`;
  }

  const archiveDate = date || dateFrom;

  return `/mb/archive/${tabCurrency}/${archiveDate}/`;
};

export const getMbArchiveBusinessDays = (dateFrom, dateTo) => {
  const start = dayjs(dateFrom, DEFAULT_API_DATE_FORMAT, true);
  const end = dayjs(dateTo, DEFAULT_API_DATE_FORMAT, true);

  if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
    return [];
  }

  const days = [];
  const current = start.clone();

  while (current.isSameOrBefore(end, 'day')) {
    if (current.isoWeekday() <= 5) {
      days.push(current.format(DEFAULT_API_DATE_FORMAT));
    }

    current.add(1, 'day');
  }

  return days;
};

export const toMbArchiveAvgDateParam = value =>
  parseMbArchiveDate(value)?.format(DEFAULT_MOBILE_DATE_FORMAT);

export const mapMbArchiveAvgItems = items => {
  if (!Array.isArray(items)) {
    return [];
  }

  return [...items]
    .map(item => {
      const archiveDate = dayjs(
        item.date,
        DEFAULT_MOBILE_DATE_FORMAT,
        true
      ).format(DEFAULT_API_DATE_FORMAT);

      return {
        ...item,
        archiveDate,
      };
    })
    .filter(item =>
      dayjs(item.archiveDate, DEFAULT_API_DATE_FORMAT, true).isValid()
    )
    .sort(
      (left, right) =>
        dayjs(left.archiveDate, DEFAULT_API_DATE_FORMAT, true).valueOf() -
        dayjs(right.archiveDate, DEFAULT_API_DATE_FORMAT, true).valueOf()
    );
};

export const sortMbArchiveDayItems = items =>
  [...items].sort((left, right) => {
    const leftTime = dayjs(left.datetime, MB_ARCHIVE_DATETIME_FORMAT, true);
    const rightTime = dayjs(right.datetime, MB_ARCHIVE_DATETIME_FORMAT, true);

    return leftTime.valueOf() - rightTime.valueOf();
  });

export const formatMbArchiveDayTime = item =>
  dayjs(item.datetime, MB_ARCHIVE_DATETIME_FORMAT, true).format(
    MB_ARCHIVE_DAY_TIME_FORMAT
  );

export const formatMbArchiveRangeDate = item =>
  dayjs(item.archiveDate, DEFAULT_API_DATE_FORMAT, true).format(
    MB_ARCHIVE_RANGE_LABEL_FORMAT
  );

export const getMbArchiveTableRows = (items, mode) => {
  if (!items?.length) {
    return [];
  }

  const sourceItems = mode === 'range' ? items : sortMbArchiveDayItems(items);

  return sourceItems.map((item, index) => ({
    key: mode === 'range' ? item.archiveDate || index : item.datetime || index,
    label:
      mode === 'range'
        ? formatMbArchiveRangeDate(item)
        : formatMbArchiveDayTime(item),
    bid: item.bid,
    ask: item.ask,
    trendBid: item.trendBid,
    trendAsk: item.trendAsk,
  }));
};
